import { useState } from 'react';
import { ExternalLink, Eye, Loader2 } from 'lucide-react';
import { useShopifyAdmin } from '@/hooks/useShopifyAdmin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MiniEventFlow } from '@/components/journey';
import { formatRelativeTime, formatDateTime } from '@/lib/utils';
import type { Journey } from '../types';
import { SessionDetailSheet } from './SessionDetailSheet';

interface JourneyTableProps {
  journeys: Journey[];
  loading?: boolean;
}

export function JourneyTable({ journeys, loading = false }: JourneyTableProps) {
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { openOrderInAdmin } = useShopifyAdmin();

  const openJourneyDetails = (journey: Journey) => {
    setSelectedJourney(journey);
    setSheetOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="relative rounded-lg border bg-white">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-lg">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[200px]">Visitor</TableHead>
              <TableHead className="w-[120px]">Started</TableHead>
              <TableHead className="w-[250px]">Journey</TableHead>
              <TableHead className="w-[100px]">Order</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {journeys.map((journey) => {
              const events = journey.events || [];
              const eventCount = events.length;
              const hasCustomer = journey.customer && (journey.customer.firstName || journey.customer.lastName || journey.customer.email);
              const customerName = hasCustomer
                ? `${journey.customer?.firstName || ''} ${journey.customer?.lastName || ''}`.trim()
                : null;

              return (
                <TableRow
                  key={journey.id}
                  className="transition-colors hover:bg-slate-50/50"
                >
                  <TableCell className="py-3">
                    {hasCustomer ? (
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {customerName || journey.customer?.email}
                        </span>
                        {customerName && journey.customer?.email && (
                          <span className="text-xs text-slate-500 truncate">
                            {journey.customer.email}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm text-slate-500">Guest</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-mono text-xs">{journey.clientId}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell className="py-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm text-slate-600">
                          {formatRelativeTime(journey.startedAt)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{formatDateTime(journey.startedAt)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TableCell>

                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <MiniEventFlow events={events} maxDots={10} />
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {eventCount} {eventCount === 1 ? 'event' : 'events'}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-3">
                    {journey.orderId ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => openOrderInAdmin(journey.orderId!)}
                            className="inline-flex items-center gap-1 font-mono text-sm text-indigo-600 hover:text-indigo-700 hover:underline cursor-pointer"
                          >
                            #{String(journey.orderId).slice(0, 8)}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Open order in Shopify Admin</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>

                  <TableCell className="py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openJourneyDetails(journey)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4 text-slate-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <SessionDetailSheet
        journey={selectedJourney}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </TooltipProvider>
  );
}
